import type { ReceivedVision } from '@sorrem/shared';
import type { UICompendium, UICompendiumChapter } from '@sorrem-client/types';

/** The two ends of the Journey's field journal, under its Journal | Compendium switch. */
export type JourneySection = 'journal' | 'compendium';

/** What the Compendium's right page shows: a chapter's title page, or a vision's page. */
export type CompendiumSelection =
    | { kind: 'chapter'; chapterId: string }
    | { kind: 'vision'; visionId: string };

/** The contents take page 1, so the first vision is on page 2. */
const FIRST_VISION_PAGE = 2;

/** Every vision in reading order: each chapter's in turn, the loose pages last. */
export function listCompendiumReadingOrder(compendium: UICompendium): ReceivedVision[] {
    return compendium.chapters.flatMap((chapter) => chapter.visions);
}

/** A vision's page number in the tome, or undefined when it isn't in it. */
export function findCompendiumPageNumber(compendium: UICompendium, visionId: string): number | undefined {
    const index = listCompendiumReadingOrder(compendium).findIndex((vision) => vision.visionId === visionId);
    return index === -1 ? undefined : index + FIRST_VISION_PAGE;
}

/** Where a vision is kept: its chapter and its index there. */
export function findCompendiumVisionPlace(
    compendium: UICompendium,
    visionId: string,
): { chapter: UICompendiumChapter; index: number } | undefined {
    for (const chapter of compendium.chapters) {
        const index = chapter.visions.findIndex((vision) => vision.visionId === visionId);
        if (index !== -1) {
            return { chapter, index };
        }
    }
    return undefined;
}

export function findCompendiumVision(compendium: UICompendium, visionId: string): ReceivedVision | undefined {
    return listCompendiumReadingOrder(compendium).find((vision) => vision.visionId === visionId);
}

export function findCompendiumChapter(compendium: UICompendium, chapterId: string): UICompendiumChapter | undefined {
    return compendium.chapters.find((chapter) => chapter.id === chapterId);
}

/**
 * The selection the tome shows: the given one while it still exists, else
 * the first vision, else the first chapter (the loose pages, when there is
 * no other), else nothing.
 */
export function resolveCompendiumSelection(compendium: UICompendium, selection: CompendiumSelection | null): CompendiumSelection | null {
    if (selection?.kind === 'vision' && findCompendiumVision(compendium, selection.visionId) !== undefined) {
        return selection;
    }
    if (selection?.kind === 'chapter' && findCompendiumChapter(compendium, selection.chapterId) !== undefined) {
        return selection;
    }
    const first = listCompendiumReadingOrder(compendium)[0];
    if (first !== undefined) {
        return { kind: 'vision', visionId: first.visionId };
    }
    const firstChapter = compendium.chapters[0];
    return firstChapter !== undefined ? { kind: 'chapter', chapterId: firstChapter.id } : null;
}

/** The route that relives a vision from the Compendium. */
export function toVisionRelivePath(visionId: string): string {
    return `/character/journey/visions/${encodeURIComponent(visionId)}`;
}

/** The painted essence emblem that marks a vision's page. */
export function toEssenceEmblemUrl(essence: ReceivedVision['essence']): string {
    return `/assets/skill-tree/emblems/essence-${essence}.png`;
}
